/**
 * Which language to show, resolved from the fallback chain.
 *
 * The order is: what the person chose, then what the clinic set as its
 * default, then what the clinic's country implies, then the first language the
 * clinic enabled. Every step is constrained to the languages the clinic has
 * actually enabled — a stored preference for a language the clinic later
 * turned off must not win.
 *
 * The server has a sibling of this (`apps/api/src/i18n/resolve-locale.ts`) that
 * resolves the language of an outbound message from the *recipient's*
 * preference; keep the two country defaults in step.
 */

/** The language a country implies when nothing more specific is set. */
export const COUNTRY_DEFAULT_LANG: Record<string, string> = {
  MD: 'ro',
  IL: 'he',
};

export function countryDefaultLang(country?: string | null): string {
  return (country && COUNTRY_DEFAULT_LANG[country]) || 'ro';
}

export interface LocaleResolution {
  /** The signed-in person's stored preference (staff or patient). */
  userPref?: string | null;
  /** The clinic's default language (`org_config.i18n.default`). */
  clinicDefault?: string | null;
  /** The languages the clinic enabled; a preference outside it is ignored. */
  enabled?: readonly string[] | null;
  /** The clinic (or branch) country, for the last-resort default. */
  country?: string | null;
}

export function resolveLocale(opts: LocaleResolution): string {
  const enabled = opts.enabled && opts.enabled.length ? opts.enabled : null;
  const allowed = (l?: string | null): l is string => !!l && (!enabled || enabled.includes(l));

  if (allowed(opts.userPref)) return opts.userPref;
  if (allowed(opts.clinicDefault)) return opts.clinicDefault;

  const byCountry = countryDefaultLang(opts.country);
  if (allowed(byCountry)) return byCountry;

  return enabled ? enabled[0] : 'ro';
}
